import { useState, useEffect, useRef, useCallback } from 'react';
import { productsService } from '../services/products';

export const useSearch = ({
    debounceMs = 300,
    minQueryLength = 2,
    maxResults = 5,
    storageKey = 'giftsy_search_history',
    maxHistoryItems = 5
} = {}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchSuggestions, setSearchSuggestions] = useState([]);
    const [searchHistory, setSearchHistory] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [showDropdown, setShowDropdown] = useState(false);

    const searchTimeoutRef = useRef(null);
    const abortControllerRef = useRef(null);

    // Load search history from localStorage on mount
    useEffect(() => {
        const savedHistory = localStorage.getItem(storageKey);
        if (savedHistory) {
            try {
                setSearchHistory(JSON.parse(savedHistory));
            } catch (error) {
                console.error('Error loading search history:', error);
            }
        }
    }, [storageKey]);

    // Cleanup function
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    // Perform search with abort controller for request cancellation
    const performSearch = useCallback(async (query) => {
        if (!query.trim() || query.length < minQueryLength) {
            setSearchResults([]);
            setSearchSuggestions([]);
            setIsSearching(false);
            return;
        }

        try {
            setIsSearching(true);
            setSearchError(null);

            // Cancel previous request if it exists
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            // Create new abort controller for this request
            abortControllerRef.current = new AbortController();

            // Search for products
            const searchResponse = await productsService.searchProducts(query, {
                limit: maxResults,
                signal: abortControllerRef.current.signal
            });

            const products = searchResponse.results || searchResponse;
            setSearchResults(products);

            // Generate search suggestions based on product names and categories
            const suggestions = products.reduce((acc, product) => {
                // Add product name if it's different from the query
                if (product.name && !acc.includes(product.name) && acc.length < maxResults) {
                    acc.push(product.name);
                }

                // Add category names
                if (product.category && product.category.name && !acc.includes(product.category.name) && acc.length < maxResults) {
                    acc.push(product.category.name);
                }

                return acc;
            }, []);

            setSearchSuggestions(suggestions);
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Search error:', error);
                setSearchError('Search failed. Please try again.');
                setSearchResults([]);
                setSearchSuggestions([]);
            }
        } finally {
            setIsSearching(false);
        }
    }, [minQueryLength, maxResults]);

    // Handle search input change with debouncing
    const handleSearchChange = useCallback((value) => {
        setSearchQuery(value);

        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Show dropdown when typing
        if (value.length > 0) {
            setShowDropdown(true);
        } else {
            setShowDropdown(false);
            setSearchResults([]);
            setSearchSuggestions([]);
        }

        // Debounce search
        searchTimeoutRef.current = setTimeout(() => {
            performSearch(value);
        }, debounceMs);
    }, [debounceMs, performSearch]);

    // Save search to history
    const saveSearchToHistory = useCallback((query) => {
        if (!query.trim()) return;

        const newHistory = [query, ...searchHistory.filter(item => item !== query)].slice(0, maxHistoryItems);
        setSearchHistory(newHistory);
        localStorage.setItem(storageKey, JSON.stringify(newHistory));
    }, [searchHistory, maxHistoryItems, storageKey]);

    // Clear search history
    const clearSearchHistory = useCallback(() => {
        setSearchHistory([]);
        localStorage.removeItem(storageKey);
    }, [storageKey]);

    // Handle input focus
    const handleInputFocus = useCallback(() => {
        if (searchQuery.length > 0 || searchHistory.length > 0) {
            setShowDropdown(true);
        }
    }, [searchQuery.length, searchHistory.length]);

    // Handle dropdown close
    const handleDropdownClose = useCallback(() => {
        setShowDropdown(false);
    }, []);

    // Handle key press
    const handleKeyPress = useCallback((e, onSearch) => {
        if (e.key === 'Enter') {
            if (onSearch) {
                onSearch(searchQuery);
            }
            saveSearchToHistory(searchQuery);
            setShowDropdown(false);
        } else if (e.key === 'Escape') {
            setShowDropdown(false);
        }
    }, [searchQuery, saveSearchToHistory]);

    return {
        // State
        searchQuery,
        searchResults,
        searchSuggestions,
        searchHistory,
        isSearching,
        searchError,
        showDropdown,

        // Actions
        setSearchQuery,
        handleSearchChange,
        handleInputFocus,
        handleDropdownClose,
        handleKeyPress,
        saveSearchToHistory,
        clearSearchHistory,
        performSearch,

        // Utilities
        setShowDropdown
    };
};

export default useSearch; 